import * as d3 from 'd3';
import { fetchDiscussionGraph, fetchGraph, GraphData } from './graphClient';

// Flarum exposes a global `app` object in the forum frontend runtime.
declare const app: any;

interface D3Node extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  degree: number;
  kind: 'node' | 'episode';
  discussionTopicId: number | null;
  /**
   * When this node represents a specific message/post within a discussion,
   * this holds the Flarum post ID. Used to deep-link into a specific post
   * via `/d/:discussion_id/:post_id`.
   */
  postId: number | null;
  /**
   * True when this node is the synthetic central node representing the
   * current discussion in a per-discussion graph.
   */
  isCentralDiscussionNode?: boolean;
}

interface D3Link extends d3.SimulationLinkDatum<D3Node> {
  id: string;
  label: string;
  edgeType: 'graph_edge' | 'episode_mention';
  source: D3Node | string;
  target: D3Node | string;
}

interface LinkGroup {
  source: string;
  target: string;
  links: D3Link[];
  curveOffset: number;
}

const BASE_COLORS = {
  nodeLowDegree: '#bbdbb8',
  nodeHighDegree: '#5f8d4c',
  episodeNode: '#c68872',
  edge: '#648d84',
  episodeMentionEdge: '#e0c330',
  nodeStroke: '#333333',
  nodeStrokeHover: '#1a1a1a',
  labelText: '#000000',
  edgeHighlight: '#c68872',
  background: 'transparent',
};

const NODE_RADIUS = {
  min: 8,
  max: 20,
  episode: 22,
};

const SIMULATION_CONFIG = {
  linkDistance: 180,
  chargeStrength: -200,
  collideRadius: 35,
  alphaDecay: 0.02,
  velocityDecay: 0.4,
};

const LABEL_MAX_CHARS_PER_LINE = 20;

export interface RenderGraphOptions {
  discussionId?: number;
}

const graphOptionsByContainer = new WeakMap<HTMLElement, RenderGraphOptions | undefined>();
const mountedGraphContainers = new Set<HTMLElement>();

function trackGraphContainer(container: HTMLElement, options?: RenderGraphOptions): void {
  mountedGraphContainers.add(container);
  graphOptionsByContainer.set(container, options);
}

export async function refreshGraph(container: HTMLElement): Promise<void> {
  const options = graphOptionsByContainer.get(container);
  await renderGraph(container, options);
}

export async function refreshAllGraphs(): Promise<void> {
  const containers = Array.from(mountedGraphContainers);

  // Prune containers that have been removed from the DOM.
  for (const container of containers) {
    if (!document.body.contains(container)) {
      mountedGraphContainers.delete(container);
    }
  }

  const activeContainers = Array.from(mountedGraphContainers);
  await Promise.all(activeContainers.map((container) => refreshGraph(container)));
}

function getNodeColor(degree: number, minDegree: number, maxDegree: number): string {
  if (maxDegree === minDegree) {
    return BASE_COLORS.nodeLowDegree;
  }
  const t = (degree - minDegree) / (maxDegree - minDegree);
  return d3.interpolateRgb(BASE_COLORS.nodeLowDegree, BASE_COLORS.nodeHighDegree)(t);
}

function getNodeRadius(degree: number, minDegree: number, maxDegree: number): number {
  if (maxDegree === minDegree) {
    return (NODE_RADIUS.min + NODE_RADIUS.max) / 2;
  }
  const t = (degree - minDegree) / (maxDegree - minDegree);
  return NODE_RADIUS.min + t * (NODE_RADIUS.max - NODE_RADIUS.min);
}

function wrapLabel(label: string): string[] {
  const words = label.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if (currentLine.length === 0) {
      currentLine = word;
    } else if (currentLine.length + 1 + word.length <= LABEL_MAX_CHARS_PER_LINE) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  if (lines.length === 0 && label.length > 0) {
    lines.push(label);
  }

  return lines;
}

function transformGraphData(
  graph: GraphData,
  options?: {
    discussionId?: number | null;
  }
): {
  nodes: D3Node[];
  links: D3Link[];
  degrees: Record<string, number>;
  minDegree: number;
  maxDegree: number;
} {
  const degrees: Record<string, number> = {};
  for (const edge of graph.edges) {
    degrees[edge.source] = (degrees[edge.source] ?? 0) + 1;
    degrees[edge.target] = (degrees[edge.target] ?? 0) + 1;
  }

  const nodes: D3Node[] = graph.nodes.map((node) => {
    const rawAttributes = node.raw?.attributes as Record<string, unknown> | undefined;
    const kind =
      rawAttributes && typeof rawAttributes.type === 'string'
        ? (rawAttributes.type as 'node' | 'episode')
        : 'node';

    let discussionTopicId: number | null = null;
    let postId: number | null = null;
    if (kind === 'episode' && rawAttributes) {
      if (typeof rawAttributes.topic_id !== 'undefined') {
        const rawTopicId = rawAttributes.topic_id;
        if (typeof rawTopicId === 'number') {
          discussionTopicId = rawTopicId;
        } else if (typeof rawTopicId === 'string') {
          const parsed = parseInt(rawTopicId, 10);
          if (!Number.isNaN(parsed) && parsed > 0) {
            discussionTopicId = parsed;
          }
        }
      }

      if (typeof rawAttributes.message_id !== 'undefined') {
        const rawMessageId = rawAttributes.message_id;
        if (typeof rawMessageId === 'number') {
          postId = rawMessageId;
        } else if (typeof rawMessageId === 'string') {
          const parsed = parseInt(rawMessageId, 10);
          if (!Number.isNaN(parsed) && parsed > 0) {
            postId = parsed;
          }
        }
      }
    }

    return {
      id: node.id,
      label: node.label,
      degree: degrees[node.id] ?? 0,
      kind,
      discussionTopicId,
      postId,
    };
  });

  const nodeIdSet = new Set(nodes.map((n) => n.id));

  const links: D3Link[] = graph.edges
    .filter((edge) => nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target))
    .map((edge) => {
      const rawAttributes = edge.raw?.attributes as Record<string, unknown> | undefined;
      const edgeType =
        rawAttributes && typeof rawAttributes.type === 'string'
          ? (rawAttributes.type as 'graph_edge' | 'episode_mention')
          : 'graph_edge';

      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        edgeType,
      };
    });
  const discussionId = options?.discussionId ?? null;

  // For discussion-scoped graphs, add a synthetic central node representing
  // the discussion itself and connect all episode nodes that belong to this
  // discussion to it. This makes the per-discussion graph feel like a single
  // connected component even if the underlying graph is sparse.
  if (discussionId && discussionId > 0) {
    const episodeNodesForDiscussion = nodes.filter(
      (n) => n.kind === 'episode' && n.discussionTopicId === discussionId
    );

    if (episodeNodesForDiscussion.length > 0) {
      const discussionNodeId = `discussion-${discussionId}`;

      // Derive a label for the central discussion node. Prefer the explicit
      // "topic" attribute we expose from the backend; fall back to a generic
      // label if not available.
      let discussionLabel: string | null = null;
      const firstEpisode = episodeNodesForDiscussion[0];
      const firstEpisodeRaw = graph.nodes.find((n) => n.id === firstEpisode.id);
      const rawAttributes = firstEpisodeRaw?.raw?.attributes as
        | Record<string, unknown>
        | undefined;

      if (rawAttributes && typeof rawAttributes.topic === 'string') {
        const topic = rawAttributes.topic.trim();
        if (topic.length > 0) {
          discussionLabel = topic;
        }
      }

      if (!discussionLabel) {
        discussionLabel = `Discussion ${discussionId}`;
      }

      const discussionNode: D3Node = {
        id: discussionNodeId,
        label: discussionLabel,
        degree: 0,
        kind: 'episode',
        discussionTopicId: discussionId,
        postId: null,
        isCentralDiscussionNode: true,
      };

      nodes.push(discussionNode);
      nodeIdSet.add(discussionNodeId);

      for (const episode of episodeNodesForDiscussion) {
        const linkId = `discussion-${discussionId}__episode__${episode.id}`;

        const syntheticLink: D3Link = {
          id: linkId,
          source: discussionNodeId,
          target: episode.id,
          label: '',
          edgeType: 'graph_edge',
        };

        links.push(syntheticLink);

        degrees[discussionNodeId] = (degrees[discussionNodeId] ?? 0) + 1;
        degrees[episode.id] = (degrees[episode.id] ?? 0) + 1;
      }
    }
  }

  // Recompute degrees and min/max based on the final set of nodes/links,
  // including any synthetic discussion edges we have added.
  let minDegree = Infinity;
  let maxDegree = -Infinity;
  for (const node of nodes) {
    const degree = degrees[node.id] ?? node.degree ?? 0;
    node.degree = degree;

    if (degree < minDegree) minDegree = degree;
    if (degree > maxDegree) maxDegree = degree;
  }
  if (!Number.isFinite(minDegree)) minDegree = 0;
  if (!Number.isFinite(maxDegree)) maxDegree = minDegree;
  if (maxDegree === minDegree) maxDegree = minDegree + 1;

  return { nodes, links, degrees, minDegree, maxDegree };
}

function groupLinks(links: D3Link[]): LinkGroup[] {
  const groups: Map<string, LinkGroup> = new Map();

  for (const link of links) {
    const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
    const targetId = typeof link.target === 'string' ? link.target : link.target.id;

    const key = [sourceId, targetId].sort().join('|');

    if (!groups.has(key)) {
      groups.set(key, {
        source: sourceId,
        target: targetId,
        links: [],
        curveOffset: 0,
      });
    }
    groups.get(key)!.links.push(link);
  }

  for (const group of groups.values()) {
    const count = group.links.length;
    group.links.forEach((link, i) => {
      if (count === 1) {
        (link as any).curveOffset = 0;
      } else {
        const step = 60 / (count - 1);
        (link as any).curveOffset = -30 + step * i;
      }
    });
  }

  return Array.from(groups.values());
}

function generateCurvedPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  curveOffset: number,
  sourceRadius: number,
  targetRadius: number
): string {
  const dx = tx - sx;
  const dy = ty - sy;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist === 0) return `M${sx},${sy}L${tx},${ty}`;

  const nx = dx / dist;
  const ny = dy / dist;

  const px = -ny;
  const py = nx;

  const startX = sx + nx * sourceRadius;
  const startY = sy + ny * sourceRadius;
  const endX = tx - nx * (targetRadius + 6);
  const endY = ty - ny * (targetRadius + 6);

  if (curveOffset === 0) {
    return `M${startX},${startY}L${endX},${endY}`;
  }

  const midX = (startX + endX) / 2 + px * curveOffset;
  const midY = (startY + endY) / 2 + py * curveOffset;

  return `M${startX},${startY}Q${midX},${midY},${endX},${endY}`;
}

function fitGraphToView(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  width: number,
  height: number,
  animate: boolean = true
): void {
  const bounds = (g.node() as SVGGElement)?.getBBox();
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return;

  const padding = 60;

  const scale = Math.min(
    (width - padding * 2) / bounds.width,
    (height - padding * 2) / bounds.height
  );

  const translateX = width / 2 - (bounds.x + bounds.width / 2) * scale;
  const translateY = height / 2 - (bounds.y + bounds.height / 2) * scale;

  const transform = d3.zoomIdentity.translate(translateX, translateY).scale(scale);

  if (animate) {
    svg
      .transition()
      .duration(750)
      .ease(d3.easeCubicInOut)
      .call(zoom.transform, transform);
  } else {
    svg.call(zoom.transform, transform);
  }
}

export async function renderGraph(container: HTMLElement, options?: RenderGraphOptions): Promise<void> {
  trackGraphContainer(container, options);
  container.classList.add('ZepGraphContainer');
  container.innerHTML = '';

  const loadingWrapper = document.createElement('div');
  loadingWrapper.className = 'ZepGraphLoading';
  const spinner = document.createElement('div');
  spinner.className = 'LoadingIndicator';
  loadingWrapper.appendChild(spinner);
  container.appendChild(loadingWrapper);

  const discussionId = options?.discussionId ?? null;
  const graph = discussionId ? await fetchDiscussionGraph(discussionId) : await fetchGraph();

  if (!graph) {
    container.textContent = 'Unable to load graph.';
    return;
  }

  if (graph.nodes.length === 0) {
    container.textContent = 'No graph data available.';
    return;
  }

  container.textContent = '';

  const { nodes, links, minDegree, maxDegree } = transformGraphData(graph, {
    discussionId,
  });

  const rect = container.getBoundingClientRect();
  const width = rect.width || 800;
  const height = rect.height || 480;

  // Prepare a color palette and, for discussion-scoped graphs, slightly tweak
  // the style by swapping the episode node color with the color used for
  // edges that originate from episode nodes.
  const colors = { ...BASE_COLORS };
  const isDiscussionScoped = typeof discussionId === 'number' && discussionId > 0;

  if (isDiscussionScoped) {
    const tmp = colors.episodeNode;
    colors.episodeNode = colors.episodeMentionEdge;
    colors.episodeMentionEdge = tmp;
  }

  const svg = d3
    .select(container)
    .append('svg')
    .attr('class', 'ZepGraphSvg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .style('cursor', 'grab');

  const defs = svg.append('defs');

  defs
    .append('marker')
    .attr('id', 'arrow')
    .attr('viewBox', '0 -5 10 10')
    .attr('refX', 8)
    .attr('refY', 0)
    .attr('markerWidth', 6)
    .attr('markerHeight', 6)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M0,-5L10,0L0,5')
    .attr('fill', colors.edge);

  defs
    .append('marker')
    .attr('id', 'arrow-episode')
    .attr('viewBox', '0 -5 10 10')
    .attr('refX', 8)
    .attr('refY', 0)
    .attr('markerWidth', 6)
    .attr('markerHeight', 6)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M0,-5L10,0L0,5')
    .attr('fill', colors.episodeMentionEdge);

  defs
    .append('marker')
    .attr('id', 'arrow-highlight')
    .attr('viewBox', '0 -5 10 10')
    .attr('refX', 8)
    .attr('refY', 0)
    .attr('markerWidth', 6)
    .attr('markerHeight', 6)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M0,-5L10,0L0,5')
    .attr('fill', colors.edgeHighlight);

  const g = svg.append('g').attr('class', 'ZepGraphMain');

  const zoom = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([0.1, 4])
    .on('zoom', (event) => {
      g.attr('transform', event.transform);
    });

  svg.call(zoom);

  groupLinks(links);

  const linkGroup = g
    .append('g')
    .attr('class', 'ZepGraphLinks')
    .selectAll('path')
    .data(links)
    .join('path')
    .attr('class', 'ZepGraphLink')
    .attr('fill', 'none')
    .attr('stroke', (d) =>
      d.edgeType === 'episode_mention' ? colors.episodeMentionEdge : colors.edge
    )
    .attr('stroke-width', 2)
    .attr('stroke-dasharray', (d) => (d.edgeType === 'episode_mention' ? '5,5' : 'none'))
    .attr('marker-end', (d) => (d.edgeType === 'episode_mention' ? 'url(#arrow-episode)' : 'url(#arrow)'))
    .attr('opacity', 0);

  const nodeGroup = g
    .append('g')
    .attr('class', 'ZepGraphNodes')
    .selectAll('g')
    .data(nodes)
    .join('g')
    .attr('class', 'ZepGraphNode')
    .attr(
      'cursor',
      (d) =>
        d.kind === 'episode' && (d.discussionTopicId || d.isCentralDiscussionNode) ? 'pointer' : 'grab'
    )
    .attr('opacity', 0);

  nodeGroup
    .append('circle')
    .attr('r', (d) =>
      d.kind === 'episode' ? NODE_RADIUS.episode : getNodeRadius(d.degree, minDegree, maxDegree)
    )
    .attr('fill', (d) => {
      // In per-discussion graphs, force the synthetic central discussion node
      // to always use the BASE_COLORS.episodeNode color, regardless of the
      // per-view color swapping we apply below.
      if (isDiscussionScoped && d.isCentralDiscussionNode) {
        return BASE_COLORS.episodeNode;
      }

      return d.kind === 'episode'
        ? colors.episodeNode
        : getNodeColor(d.degree, minDegree, maxDegree);
    })
    .attr('stroke', colors.nodeStroke)
    .attr('stroke-width', 1.5);

  nodeGroup.each(function (d) {
    const nodeElement = d3.select(this);
    const lines = wrapLabel(d.label);
    const lineHeight = 12;
    const r =
      d.kind === 'episode' ? NODE_RADIUS.episode : getNodeRadius(d.degree, minDegree, maxDegree);
    const startY = r + 14;

    const textElement = nodeElement
      .append('text')
      .attr('class', 'ZepGraphNodeLabel')
      .attr('text-anchor', 'middle')
        .attr('fill', colors.labelText)
      .attr('font-size', '10px')
      .attr('pointer-events', 'none');

    lines.forEach((line, i) => {
      textElement.append('tspan').attr('x', 0).attr('dy', i === 0 ? startY : lineHeight).text(line);
    });
  });

  const simulation = d3
    .forceSimulation<D3Node>(nodes)
    .force(
      'link',
      d3.forceLink<D3Node, D3Link>(links).id((d) => d.id).distance(SIMULATION_CONFIG.linkDistance)
    )
    .force('charge', d3.forceManyBody().strength(SIMULATION_CONFIG.chargeStrength))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collide', d3.forceCollide().radius(SIMULATION_CONFIG.collideRadius))
    .alphaDecay(SIMULATION_CONFIG.alphaDecay)
    .velocityDecay(SIMULATION_CONFIG.velocityDecay);

  let hasInitialFit = false;

  simulation.on('tick', () => {
    linkGroup.attr('d', (d) => {
      const source = d.source as D3Node;
      const target = d.target as D3Node;
      const curveOffset = (d as any).curveOffset || 0;

      const sourceRadius =
        source.kind === 'episode'
          ? NODE_RADIUS.episode
          : getNodeRadius(source.degree, minDegree, maxDegree);
      const targetRadius =
        target.kind === 'episode'
          ? NODE_RADIUS.episode
          : getNodeRadius(target.degree, minDegree, maxDegree);

      return generateCurvedPath(
        source.x!,
        source.y!,
        target.x!,
        target.y!,
        curveOffset,
        sourceRadius,
        targetRadius
      );
    });

    nodeGroup.attr('transform', (d) => `translate(${d.x},${d.y})`);

    if (!hasInitialFit && simulation.alpha() < 0.3) {
      hasInitialFit = true;
      fitGraphToView(svg, g, zoom, width, height, true);
    }
  });

  simulation.on('end', () => {
    if (!hasInitialFit) {
      hasInitialFit = true;
      fitGraphToView(svg, g, zoom, width, height, true);
    }
  });

  const drag = d3
    .drag<SVGGElement, D3Node>()
    .on('start', (event, d) => {
      if (!event.active) simulation.alphaTarget(0.05).restart();
      d.fx = d.x;
      d.fy = d.y;
      svg.style('cursor', 'grabbing');
    })
    .on('drag', (event, d) => {
      d.fx = event.x;
      d.fy = event.y;
    })
    .on('end', (event, d) => {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
      svg.style('cursor', 'grab');
    });

  nodeGroup.call(drag as any);

  nodeGroup
    .on('mouseenter', function (event, d) {
      const node = d3.select(this);

      node
        .select('circle')
        .transition()
        .duration(150)
        .attr('r', (d: any) => {
          const baseRadius =
            d.kind === 'episode'
              ? NODE_RADIUS.episode
              : getNodeRadius(d.degree, minDegree, maxDegree);
          return baseRadius * 1.3;
        })
        .attr('stroke', colors.nodeStrokeHover)
        .attr('stroke-width', 2.5);

      linkGroup
        .filter((l) => {
          const source = l.source as D3Node;
          const target = l.target as D3Node;
          return source.id === d.id || target.id === d.id;
        })
        .transition()
        .duration(150)
        .attr('stroke', colors.edgeHighlight)
        .attr('stroke-width', 3)
        .attr('marker-end', 'url(#arrow-highlight)');

      linkGroup
        .filter((l) => {
          const source = l.source as D3Node;
          const target = l.target as D3Node;
          return source.id !== d.id && target.id !== d.id;
        })
        .transition()
        .duration(150)
        .attr('opacity', 0.2);

      nodeGroup
        .filter((n) => {
          const isConnected = links.some((l) => {
            const source = l.source as D3Node;
            const target = l.target as D3Node;
            return (
              (source.id === d.id && target.id === n.id) ||
              (target.id === d.id && source.id === n.id)
            );
          });
          return n.id !== d.id && !isConnected;
        })
        .transition()
        .duration(150)
        .attr('opacity', 0.3);
    })
    .on('mouseleave', function () {
      const node = d3.select(this);

      node
        .select('circle')
        .transition()
        .duration(150)
        .attr('r', (d: any) =>
          d.kind === 'episode'
            ? NODE_RADIUS.episode
            : getNodeRadius(d.degree, minDegree, maxDegree)
        )
        .attr('stroke', colors.nodeStroke)
        .attr('stroke-width', 1.5);

      linkGroup
        .transition()
        .duration(150)
        .attr('stroke', (l) =>
          l.edgeType === 'episode_mention' ? colors.episodeMentionEdge : colors.edge
        )
        .attr('stroke-width', 2)
        .attr('opacity', 1)
        .attr('marker-end', (l) =>
          l.edgeType === 'episode_mention' ? 'url(#arrow-episode)' : 'url(#arrow)'
        );

      nodeGroup.transition().duration(150).attr('opacity', 1);
    });

  nodeGroup.on('click', (event, d) => {
    if (d.kind === 'episode' && d.discussionTopicId) {
      const baseUrl = app.forum.attribute('baseUrl') as string;
      if (baseUrl) {
        const discussionPath = `${baseUrl}/d/${d.discussionTopicId}`;

        // For regular episode/message nodes, deep-link to the specific post when we know it.
        // For the synthetic central discussion node, keep linking to the discussion root.
        const url =
          !d.isCentralDiscussionNode && d.postId && d.postId > 0
            ? `${discussionPath}/${d.postId}`
            : discussionPath;

        window.location.href = url;
      }
    }
  });

  nodeGroup.transition().duration(700).ease(d3.easeCubicOut).attr('opacity', 1);

  linkGroup
    .transition()
    .delay(150)
    .duration(600)
    .ease(d3.easeCubicOut)
    .attr('opacity', 1);
}

